// 📁 components/categoria/ExportButtons.js
import React, { useState } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import {
  PictureAsPdf,
  GetApp,
  Assignment,
  People,
  BarChart,
  TableChart
} from '@mui/icons-material';
import { theme } from '../../styles/CategoriaStyles';
import {
  exportCategoriaRepresentantesPDF,
  exportAsociacionCategoriasPDF,
  exportJuntaDirectivaPDF,
  exportResumenEstadisticoPDF
} from '../../utils/pdfExportUtils';

const ExportButtons = ({
  selectedAsociacion,
  selectedCategoria,
  representantes = [],
  categorias = [],
  asociaciones = [],
  departamentos = [],
  municipios = [], // ⭐ AGREGAR ESTE PARÁMETRO
  context = 'general' // 'general', 'categoria', 'junta'
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const showNotification = (message, severity = 'success') => {
    setNotification({ open: true, message, severity });
  };

  const handleExport = async (exportType) => {
    setLoading(true);
    setAnchorEl(null);

    try {
      switch (exportType) {
        case 'categoria':
          if (selectedCategoria && representantes.length > 0) {
            const representantesCategoria = representantes.filter(
              (rep) =>
                rep.categorias?.some((categoria) => categoria.Id === selectedCategoria.Id && categoria.Estado !== 'Inactivo') &&
                rep.estatus !== 'INHABILITADO'
            );
            exportCategoriaRepresentantesPDF(
              selectedCategoria,
              representantesCategoria,
              asociaciones,
              departamentos,
              municipios // ⭐ AGREGAR AQUÍ
            );
            showNotification(`PDF generado: ${selectedCategoria.Descripcion}`);
          }
          break;

        case 'asociacion':
          if (selectedAsociacion) {
            const categoriasAsociacion = categorias.filter((cat) => {
              return representantes.some(
                (rep) =>
                  rep.cod_asociacion === selectedAsociacion.cod_asociacion &&
                  rep.categorias?.some((categoria) => categoria.Id === cat.Id && categoria.Estado !== 'Inactivo') &&
                  rep.estatus !== 'INHABILITADO'
              );
            });

            const representantesAsociacion = representantes.filter(
              (rep) =>
                rep.cod_asociacion === selectedAsociacion.cod_asociacion &&
                rep.estatus !== 'INHABILITADO'
            );

            exportAsociacionCategoriasPDF(
              selectedAsociacion,
              categoriasAsociacion,
              representantesAsociacion,
              departamentos,
              municipios // ⭐ AGREGAR AQUÍ
            );
            showNotification(`PDF generado: ${selectedAsociacion.nombre_abreviado}`);
          }
          break;

        case 'junta':
          if (selectedAsociacion) {
            const juntaDirectiva = representantes.filter(
              (rep) =>
                rep.cod_asociacion === selectedAsociacion.cod_asociacion &&
                rep.categorias?.some((cat) => cat.Id === 4 && cat.Estado !== 'Inactivo') &&
                rep.estatus !== 'INHABILITADO'
            );

            exportJuntaDirectivaPDF(
              selectedAsociacion,
              juntaDirectiva,
              departamentos,
              municipios // ⭐ AGREGAR AQUÍ
            );
            showNotification(`PDF de Junta Directiva generado: ${selectedAsociacion.nombre_abreviado}`);
          }
          break;

        case 'resumen':
          const representantesActivos = representantes.filter(
            (rep) => rep.estatus !== 'INHABILITADO'
          );
          exportResumenEstadisticoPDF(
            asociaciones,
            representantesActivos,
            categorias,
            departamentos,
            municipios // ⭐ AGREGAR AQUÍ
          );
          showNotification('Resumen estadístico generado');
          break;

        default:
          showNotification('Tipo de exportación no válido', 'error');
      }
    } catch (error) {
      console.error('Error al generar PDF:', error);
      showNotification('Error al generar el PDF', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Verificar qué opciones mostrar según el contexto
  const getMenuOptions = () => {
    const options = [];

    // Opciones específicas de asociación
    if (selectedAsociacion) {
      options.push({
        key: 'asociacion',
        icon: <Assignment />,
        text: `Reporte Completo - ${selectedAsociacion.nombre_abreviado}`,
        description: 'Todas las categorías de esta asociación',
        disabled: false
      });

      // Solo mostrar opción de Junta Directiva si hay miembros activos
      const tieneJuntaDirectiva = representantes.some(
        (rep) =>
          rep.cod_asociacion === selectedAsociacion.cod_asociacion &&
          rep.categorias?.some((cat) => cat.Id === 4 && cat.Estado !== 'Inactivo') &&
          rep.estatus !== 'INHABILITADO'
      );

      if (tieneJuntaDirectiva) {
        options.push({
          key: 'junta',
          icon: <People />,
          text: 'Junta Directiva',
          description: `Organigrama de ${selectedAsociacion.nombre_abreviado}`,
          disabled: false
        });
      }
    }

    // Opción específica de categoría
    if (selectedCategoria && representantes.length > 0) {
      const representantesCategoria = representantes.filter(
        (rep) =>
          rep.categorias?.some((categoria) => categoria.Id === selectedCategoria.Id && categoria.Estado !== 'Inactivo') &&
          rep.estatus !== 'INHABILITADO'
      );
      options.push({
        key: 'categoria',
        icon: <TableChart />,
        text: `Categoría - ${selectedCategoria.Descripcion}`,
        description: `${representantesCategoria.length} representantes`,
        disabled: false
      });
    }

    return options;
  };

  const menuOptions = getMenuOptions();

  // No mostrar botón si no hay opciones disponibles
  if (menuOptions.length === 0) {
    return null;
  }

  return (
    <>
      <Button
        variant="contained"
        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PictureAsPdf />}
        onClick={handleClick}
        disabled={loading}
        sx={{
          backgroundColor: theme.palette.accent.main,
          color: 'white',
          '&:hover': {
            backgroundColor: theme.palette.accent.dark,
          },
          '&:disabled': {
            backgroundColor: theme.palette.border.light,
          }
        }}
      >
        {loading ? 'Generando...' : 'Exportar PDF'}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            minWidth: 280,
            maxWidth: 350,
            '& .MuiMenuItem-root': {
              px: 2,
              py: 1.5,
            }
          }
        }}
      >
        {menuOptions.map((option, index) => (
          <React.Fragment key={option.key}>
            <MenuItem
              onClick={() => handleExport(option.key)}
              disabled={option.disabled}
            >
              <ListItemIcon>
                {option.icon}
              </ListItemIcon>
              <ListItemText
                primary={option.text}
                secondary={option.description}
                primaryTypographyProps={{
                  variant: 'body2',
                  fontWeight: 600
                }}
                secondaryTypographyProps={{
                  variant: 'caption',
                  color: 'text.secondary'
                }}
              />
            </MenuItem>
            {index < menuOptions.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </Menu>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ExportButtons;